import _ from "lodash";
import { graphql, compose } from "lib/apolloClient";

import withLoader from "UIComponents/GqlWithLoader";
import DualPanelManageMembers from "AppComponents/DualPanelManageMembers";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  getCourseUsersQueryV2,
  getGradeUsersQueryV2,
  getYearGroupUsersQuery,
  getOrganizationCourseUsersQueryV2,
  getMessageV2UsersWithStudentParentQuery,
} from "MessagingV2/modules/MessagingQuery";
import {
  getCourseUsersFromCacheV2,
  getGradeUsersV2FromCache,
  getYearGroupUsersFromCache,
  getOrganizationCourseUsersFromCacheV2,
  getMessageV2UsersWithStudentParentFromCache,
} from "MessagingV2/modules/MessagingGraphqlHelper";
import { getOrganizationId, getUserId } from "modules/CommonSelectors";
import {
  PARENT,
  STAFF,
  STUDENT,
  CLASSES,
  YEAR_GROUP,
  GRADE,
} from "Constants/stringConstants";
import {
  CHAT_STATUS_ENUM,
  isMessagingCurriculumWiseSplitEnabled,
  isMessagingRnpEnabled,
  isCreateChatsAndChannelsEnableForAll,
  RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
  getChatsAndChannelEnabledCurriculum,
} from "MessagingV2/modules/Utils";
import { prepareLoadMoreFeed } from "Utils";
import {
  getCoursesCohorts,
  getYearGroupCohorts,
  getGradesCohorts,
} from "AppComponents/DualPanelManageMembers/module/utils";

const USER_FILTER_ORDER = [STAFF, STUDENT, PARENT];

const getUserFilterByOrder = typeFilter => {
  const userFilter = _.isEmpty(typeFilter)
    ? [STAFF, STUDENT, PARENT]
    : _.filter(typeFilter, type => [STAFF, STUDENT, PARENT].includes(type));
  return _.sortBy(userFilter, type => _.indexOf(USER_FILTER_ORDER, type));
};

const UsersAndCohortsQueryWrapper = compose(
  graphql(getMessageV2UsersWithStudentParentQuery, {
    name: "getMessageV2UsersWithStudentParent",
    skip: ({ typeFilter }) =>
      !_.isEmpty(typeFilter) &&
      !_.some(typeFilter, type => [STAFF, STUDENT, PARENT].includes(type)),

    options: props => {
      const { searchText, includePaidUsersOnly = false, typeFilter } = props;
      const userType = getUserFilterByOrder(typeFilter);
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          portalType: "PLANNER",
          input: {
            first: 10,
            userType,
            searchText,
            userStatus: "ACTIVE",
            chatStatus: CHAT_STATUS_ENUM.ALL,
            includePaidUsersOnly,
            includeInitiatedChatUsers: false,
          },
        },
      };
    },
    props: ({ getMessageV2UsersWithStudentParent }) => {
      const queryVariables = _.get(
        getMessageV2UsersWithStudentParent,
        "variables",
        {}
      );
      const cachedData = getMessageV2UsersWithStudentParentFromCache(
        queryVariables
      );

      const userNodeList = _.get(cachedData, "messageV2Users.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "messageV2Users.pageInfo.hasNextPage",
        false
      );

      const membersParams = {
        userNodeList,
        hasNextPage,
        networkStatus: getMessageV2UsersWithStudentParent["networkStatus"],
        isFetchingMore: _.includes(
          [3],
          getMessageV2UsersWithStudentParent["networkStatus"]
        ),

        loadMoreFeed: prepareLoadMoreFeed({
          query: getMessageV2UsersWithStudentParentQuery,
          variables: {
            ...queryVariables,
            input: {
              ..._.get(queryVariables, "input", {}),
              before: _.get(cachedData, "messageV2Users.pageInfo.endCursor"),
            },
          },
          queryData: getMessageV2UsersWithStudentParent,
          key: "messageV2Users",
        }),
      };

      return {
        isData: !_.isEmpty(cachedData),
        isLoading: _.includes(
          [1, 2],
          getMessageV2UsersWithStudentParent["networkStatus"]
        ),
        membersParams,
      };
    },
  }),
  graphql(getOrganizationCourseUsersQueryV2, {
    name: "getOrganizationCourseUsers",
    skip: ({ typeFilter }) => {
      const rnpPermission = isMessagingRnpEnabled() || !getIsAdminByRole();
      if (rnpPermission) {
        return true;
      }
      return !_.isEmpty(typeFilter) && !_.includes(typeFilter, CLASSES);
    },
    options: props => {
      const { searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          first: 10,
          portalType: "PLANNER",
          filters: {
            searchText,
            includeFromPaidConnectCurriculumsOnly: isMessagingCurriculumWiseSplitEnabled(),
          },
        },
      };
    },
    props: ({ getOrganizationCourseUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getOrganizationCourseUsers, "variables", {});
      const cachedData = getOrganizationCourseUsersFromCacheV2(
        getOrganizationCourseUsers.variables
      );

      const courses = _.get(cachedData, "courses.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "courses.pageInfo.hasNextPage",
        false
      );

      const coursesCohorts = getCoursesCohorts(courses);

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        networkStatus: getOrganizationCourseUsers["networkStatus"],

        loadMoreFeed: prepareLoadMoreFeed({
          query: getOrganizationCourseUsersQueryV2,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "courses.pageInfo.endCursor"),
          },
          queryData: getOrganizationCourseUsers,
          key: "courses",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationCourseUsers["networkStatus"]) ||
          isLoading,
        courseUserParams,
      };
    },
  }),
  graphql(getCourseUsersQueryV2, {
    name: "getCourseUsers",
    skip: ({ typeFilter }) => {
      const rnpPermission = !isMessagingRnpEnabled() && getIsAdminByRole();
      if (rnpPermission) {
        return true;
      }
      return !_.isEmpty(typeFilter) && !_.includes(typeFilter, CLASSES);
    },
    options: props => {
      const { searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: getUserId(),
          organizationId: getOrganizationId(),
          first: 10,
          portalType: "PLANNER",
          filters: {
            searchText,
            includeFromPaidConnectCurriculumsOnly: isMessagingCurriculumWiseSplitEnabled(),
            ...(isMessagingRnpEnabled()
              ? {
                  fetchOnlyMappedCourses: false,
                  controlParameters: [
                    RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
                  ],
                }
              : {}),
          },
        },
      };
    },
    props: ({ getCourseUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getCourseUsers, "variables", {});
      const cachedData = getCourseUsersFromCacheV2(getCourseUsers.variables);

      const courses = _.get(cachedData, "coursesV2.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "coursesV2.pageInfo.hasNextPage",
        false
      );

      const coursesCohorts = getCoursesCohorts(courses);

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        networkStatus: getCourseUsers["networkStatus"],
        loadMoreFeed: prepareLoadMoreFeed({
          query: getCourseUsersQueryV2,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "coursesV2.pageInfo.endCursor"),
          },
          queryData: getCourseUsers,
          key: "coursesV2",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getCourseUsers["networkStatus"]) || isLoading,
        courseUserParams,
      };
    },
  }),

  graphql(getYearGroupUsersQuery, {
    name: "getYearGroupUsers",
    skip: ({ typeFilter }) => {
      const rnpPermission =
        isMessagingRnpEnabled() && !isCreateChatsAndChannelsEnableForAll();

      if (rnpPermission) {
        return true;
      }

      return !_.isEmpty(typeFilter) && !_.includes(typeFilter, YEAR_GROUP);
    },
    options: props => {
      const { searchText } = props;

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          first: 3,
          portalType: "PLANNER",
          filters: {
            searchText,
            includeFromPaidConnectCurriculumsOnly: isMessagingCurriculumWiseSplitEnabled(),
            ...(isMessagingRnpEnabled()
              ? {
                  curriculumProgramIds: getChatsAndChannelEnabledCurriculum(),
                }
              : {}),
          },
        },
      };
    },
    props: ({ getYearGroupUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const cachedData = getYearGroupUsersFromCache(
        getYearGroupUsers.variables
      );

      const yearGroups = _.get(cachedData, "yearGroups", []);
      const hasNextPage = false;

      const yearGroupsCohorts = getYearGroupCohorts(yearGroups);
      const yearGroupUserParams = {
        cohorts: yearGroupsCohorts,
        hasNextPage,
        networkStatus: getYearGroupUsers["networkStatus"],
        loadMoreFeed: _.noop,
      };

      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getYearGroupUsers["networkStatus"]) || isLoading,
        yearGroupUserParams,
      };
    },
  }),
  graphql(getGradeUsersQueryV2, {
    name: "getGradeUsers",
    skip: ({ typeFilter }) => {
      const rnpPermission =
        isMessagingRnpEnabled() && !isCreateChatsAndChannelsEnableForAll();
      if (rnpPermission) {
        return true;
      }

      return !_.isEmpty(typeFilter) && !_.includes(typeFilter, GRADE);
    },
    options: props => {
      const { searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          first: 10,
          portalType: "PLANNER",
          filters: {
            searchText,
            includeFromPaidConnectCurriculumsOnly: isMessagingCurriculumWiseSplitEnabled(),
            ...(isMessagingRnpEnabled()
              ? {
                  curriculumProgramIds: getChatsAndChannelEnabledCurriculum(),
                }
              : {}),
          },
        },
      };
    },
    props: ({ getGradeUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getGradeUsers, "variables", {});
      const cachedData = getGradeUsersV2FromCache(getGradeUsers.variables);

      const grades = _.get(cachedData, "grades", []);
      const hasNextPage = _.get(
        cachedData,
        "grades.pageInfo.hasNextPage",
        false
      );

      const gradesCohorts = getGradesCohorts(grades);
      const gradeUserParams = {
        cohorts: gradesCohorts,
        hasNextPage,
        networkStatus: getGradeUsers["networkStatus"],
        loadMoreFeed: prepareLoadMoreFeed({
          query: getGradeUsersQueryV2,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "grades.pageInfo.endCursor"),
          },
          queryData: getGradeUsers,
          key: "courses",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getGradeUsers["networkStatus"]) || isLoading,
        gradeUserParams,
      };
    },
  }),
  withLoader
)(DualPanelManageMembers.CohortList);

export const metadataProps = {
  emptyStateSubtitle: "common:add_members_v3_search_empty_state_subtitle",
  countUsersSelectedText: "common:count_member_selected",
  selectedUsersListText: "common:selected_members_list",
  disabledUserTooltipText: "common:user_already_exists_in_channel",
  disabledNestedFamilyTooltipText:
    "common:family_member_already_exist_in_channel",
  filterLabelText: {
    label: "common:group_by",
    noneText: "common:all",
  },
};

export default UsersAndCohortsQueryWrapper;
